import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";

const VENDOR_TYPES = ["Brand Owner", "Manufacturer", "Distributor", "Wholesaler", "Importer", "Other"];
const LOOKING_FOR = [
  "Sell products to The Spotters",
  "Become an authorized supplier/distributor",
  "Business collaboration",
  "Other",
];

const EMPTY = {
  business_name: "", contact_name: "", mobile: "", email: "", city: "",
  vendor_type: "", products: "", brands: "", looking_for: "", about: "", website: "",
  consent: false,
};

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}{required && <span className="text-[hsl(var(--accent))]"> *</span>}
      </label>
      {children}
      {hint && <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function ChipGroup({ options, value, onChange, testid }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          data-testid={`${testid}-${o}`}
          onClick={() => onChange(o)}
          className={`px-3 py-2 border font-mono text-[11px] uppercase tracking-wide transition-colors ${
            value === o
              ? "bg-foreground text-background border-foreground"
              : "border-foreground/30 hover:border-foreground"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function BecomeVendor() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.vendor_type) return toast.error("Please tell us what type of business you are.");
    if (!f.looking_for) return toast.error("Please tell us what you're looking for.");
    if (!f.consent) return toast.error("Please confirm the declaration to submit.");
    setBusy(true);
    try {
      await api.post("/vendor-enquiry", f);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit enquiry");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-24">
        <div className="border-2 border-foreground p-12 md:p-16 tag-punch pl-16 md:pl-20">
          <div className="w-14 h-14 border-2 border-[hsl(var(--accent))] flex items-center justify-center mb-8">
            <Check size={28} strokeWidth={2.5} className="text-[hsl(var(--accent))]" />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Enquiry Received</p>
          <h1 className="font-serif text-5xl md:text-7xl mt-3 tracking-tighter leading-[0.9]">
            Thanks for your interest in partnering with The Spotters.
          </h1>
          <p className="mt-6 max-w-xl text-muted-foreground text-lg">
            Our team has received your enquiry and will review your details. We'll contact you if
            there's a suitable opportunity to work together.
          </p>
          <div className="mt-10 flex gap-4">
            <Link to="/" className="border border-foreground px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-foreground hover:text-background">
              Back to Home
            </Link>
            <button
              onClick={() => { setF(EMPTY); setDone(false); }}
              className="font-mono text-xs uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground"
            >
              Submit another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-16 md:py-24">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Become a Vendor · Enquiry</p>
      <h1 className="font-serif text-6xl md:text-8xl mt-3 tracking-tighter leading-[0.88]">
        Partner with<br />The Spotters.
      </h1>
      <p className="mt-8 max-w-2xl text-muted-foreground text-lg">
        Are you a brand, manufacturer, distributor or supplier looking to sell your products through
        The Spotters? Tell us a little about your business and our team will get in touch with you.
      </p>

      <form onSubmit={submit} className="mt-14 space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Business / Brand Name" required>
            <input data-testid="enq-business_name" value={f.business_name} onChange={set("business_name")}
              placeholder="Enter your business or brand name" className="underline-input" required />
          </Field>
          <Field label="Your Name" required>
            <input data-testid="enq-contact_name" value={f.contact_name} onChange={set("contact_name")}
              placeholder="Enter your full name" className="underline-input" required />
          </Field>
          <Field label="Mobile Number" required>
            <input data-testid="enq-mobile" value={f.mobile} onChange={set("mobile")}
              placeholder="+91 XXXXX XXXXX" className="underline-input" required />
          </Field>
          <Field label="Email Address">
            <input data-testid="enq-email" type="email" value={f.email} onChange={set("email")}
              placeholder="Enter your email address" className="underline-input" />
          </Field>
          <Field label="City / Location" required>
            <input data-testid="enq-city" value={f.city} onChange={set("city")}
              placeholder="Enter your city" className="underline-input" required />
          </Field>
          <Field label="Website / Catalogue / Instagram">
            <input data-testid="enq-website" value={f.website} onChange={set("website")}
              placeholder="Optional" className="underline-input" />
          </Field>
        </div>

        <Field label="You are a" required>
          <ChipGroup options={VENDOR_TYPES} value={f.vendor_type}
            onChange={(v) => setF({ ...f, vendor_type: v })} testid="enq-type" />
        </Field>

        <Field label="What products do you deal in?" required>
          <input data-testid="enq-products" value={f.products} onChange={set("products")}
            placeholder="e.g. Sports Footwear, Sneakers, Apparel, Sports Equipment..." className="underline-input" required />
        </Field>

        <Field label="Brands you deal in">
          <input data-testid="enq-brands" value={f.brands} onChange={set("brands")}
            placeholder="Enter brand names" className="underline-input" />
        </Field>

        <Field label="What are you looking for?" required>
          <ChipGroup options={LOOKING_FOR} value={f.looking_for}
            onChange={(v) => setF({ ...f, looking_for: v })} testid="enq-looking" />
        </Field>

        <Field label="Tell us about your business">
          <textarea data-testid="enq-about" value={f.about} onChange={set("about")}
            placeholder="Briefly tell us about your products, brands, distribution network, etc."
            className="underline-input min-h-[120px] w-full" />
        </Field>

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" data-testid="enq-consent" checked={f.consent}
            onChange={(e) => setF({ ...f, consent: e.target.checked })}
            className="mt-1 w-4 h-4 accent-[hsl(var(--accent))]" />
          <span className="text-sm text-muted-foreground">
            I confirm that the information provided above is accurate and authorize The Spotters to
            contact me regarding my enquiry.
          </span>
        </label>

        <div className="pt-2">
          <button data-testid="enq-submit" type="submit" disabled={busy}
            className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-10 py-5 font-mono text-xs uppercase tracking-[0.24em] hover:bg-foreground hover:text-background disabled:opacity-50">
            {busy ? "Submitting..." : "Submit Enquiry"}
          </button>
          <p className="mt-6 font-mono text-[11px] text-muted-foreground">
            Already spoken with our team about onboarding? You'll receive a private registration link
            to complete your vendor profile.
          </p>
        </div>
      </form>
    </div>
  );
}
